import * as Sentry from '@sentry/react';
import {
  GLITCHTIP_DSN,
  GLITCHTIP_ENVIRONMENT,
  GLITCHTIP_TRACES_SAMPLE_RATE,
} from 'configs/GlitchTipConfig';
import { generateRequestId, REQUEST_ID_HEADER } from 'utils/requestId';

const TEST_TIMEOUT_MS = 10000;

const beforeSend = (event, hint) => {
  const error = hint?.originalException;

  if (!error?.isAxiosError) {
    return event;
  }

  const status = error.response?.status;

  if (status && status < 500) {
    return null;
  }

  const requestId = error.config?.headers?.[REQUEST_ID_HEADER];

  if (requestId) {
    event.tags = { ...event.tags, request_id: requestId };
  }

  return event;
};

export const initGlitchTip = () => {
  if (!GLITCHTIP_DSN) {
    return;
  }

  Sentry.init({
    dsn: GLITCHTIP_DSN,
    environment: GLITCHTIP_ENVIRONMENT,
    tracesSampleRate: GLITCHTIP_TRACES_SAMPLE_RATE,
    integrations: (defaults) => defaults.filter((integration) => integration.name !== 'BrowserSession'),
    beforeSend,
  });
};

export const getGlitchTipTarget = () => {
  const client = Sentry.getClient();

  if (!client) {
    return null;
  }

  return {
    host: client.getDsn()?.host,
    environment: client.getOptions().environment,
  };
};

export const testGlitchTipConnection = () => {
  const client = Sentry.getClient();

  if (!client) {
    return Promise.resolve({ status: 'disabled' });
  }

  return new Promise((resolve) => {
    let eventId;

    const finish = (result) => {
      clearTimeout(timer);
      unsubscribe();
      resolve({ ...result, eventId });
    };

    const timer = setTimeout(() => finish({ status: 'timeout' }), TEST_TIMEOUT_MS);

    const unsubscribe = client.on('afterSendEvent', (event, response) => {
      if (event.event_id !== eventId) {
        return;
      }

      const statusCode = response?.statusCode;

      if (!statusCode) {
        finish({ status: 'unreachable' });
      } else if (statusCode >= 200 && statusCode < 300) {
        finish({ status: 'success', statusCode });
      } else {
        finish({ status: 'rejected', statusCode });
      }
    });

    eventId = Sentry.captureMessage(`GlitchTip connection test ${generateRequestId()}`, {
      level: 'info',
      fingerprint: ['glitchtip-connection-test'],
      tags: { connection_test: 'true' },
    });
  });
};
